// Navbar.js
import { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { useRegions } from "../../context/RegionsContext";
import { useTenant } from "../../context/TenantContext";
import * as allauth from "../../library/allauth";
import XRoadz_Logo from "../../img/XRoadz_Logo.png";
import UserDropdown from "./UserDropdown";
import Sidebar from "./Sidebar";
import NotificationBell from "../ui/NotificationBell";
import {
  Search,
  MapPin,
  Building2,
  ChevronDown,
  ChevronUp,
  Bell,
  CircleQuestionMark,
  PanelLeftOpen,
  PanelLeftClose,
  X,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

export default function Navbar() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const {
    regions,
    isLoading: regionsLoading,
    selectedRegion,
    setSelectedRegion,
  } = useRegions();
  const {
    available: tenants,
    activeCustomerId,
    isMulti,
    selectTenant,
  } = useTenant();
  const [userGroups, setUserGroups] = useState([]);
  const [userFirstName, setUserFirstName] = useState("");
  const [userLastName, setUserLastName] = useState("");
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isLocationOpen, setIsLocationOpen] = useState(false);
  const [isTenantOpen, setIsTenantOpen] = useState(false);
  const [isSwitchingTenant, setIsSwitchingTenant] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearchOpen, setIsSearchOpen] = useState(false);

  const activeTenant = tenants.find((t) => t.customer_id === activeCustomerId);

  const handleSelectTenant = async (customerId) => {
    setIsTenantOpen(false);
    if (customerId === activeCustomerId) return;
    setIsSwitchingTenant(true);
    try {
      await selectTenant(customerId);
    } finally {
      setIsSwitchingTenant(false);
    }
  };

  useEffect(() => {
    fetchUserGroups();
  }, [user]);

  const fetchUserGroups = async () => {
    try {
      const response = await allauth.getUserGroups();
      if (response.status === 200) {
        setUserGroups(response.data.groups || []);
        setUserFirstName(response.data.first_name || "");
        setUserLastName(response.data.last_name || "");
        if (response.data.features) {
          sessionStorage.setItem(
            "userFeatures",
            JSON.stringify(response.data.features),
          );
        }
      }
    } catch (error) {
      console.error("Error fetching user groups:", error);
    }
  };

const getUserRole = () => {
  if (userGroups.includes("xroadz_admin"))  return "Super Admin";
  if (userGroups.includes("customer_admin")) return "Customer Admin";
  if (userGroups.includes("developer"))      return "Developer";
  return "User";
};

const getRoleBadgeColor = () => {
  if (userGroups.includes("xroadz_admin"))  return "bg-purple-100 text-purple-700";
  if (userGroups.includes("customer_admin")) return "bg-blue-100 text-blue-700";
  if (userGroups.includes("developer"))      return "bg-blue-100 text-blue-700";
  return "bg-gray-100 text-[#667085]";
};

const getAvatarColor = () => {
  if (userGroups.includes("xroadz_admin"))  return "bg-purple-500";
  if (userGroups.includes("customer_admin")) return "bg-blue-500";
  if (userGroups.includes("developer"))      return "bg-blue-500";
  return "bg-[#7c837b]";
};

  const go_to_support = () => {
    navigate("/support");
  };

  return (
    <>
      <div className="flex items-start flex-grow fixed justify-start w-full h-14 bg-white border border-[#e9f1e8] z-[9998]">
        {/* Logo Section - Dynamic width */}
        <div
          className={`flex self-stretch flex-grow-0 flex-shrink-0 items-center justify-between h-full px-2 sm:px-4 relative transition-all duration-300 ${
            isSidebarOpen
              ? "w-[280px] sm:w-[332px] bg-white border-r border-[#e9f1e8]"
              : "w-[120px] sm:w-[150px]"
          }`}
        >
          <button onClick={() => (window.location.href = "/home")}>
            <div
              className="w-[60px] sm:w-[76px] h-8 sm:h-10 bg-contain bg-no-repeat bg-center"
              style={{ backgroundImage: `url(${XRoadz_Logo})` }}
            />
          </button>

          <button
            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
            className={`flex items-center gap-2 p-1 transition-all duration-300 ${
              isSidebarOpen ? "mr-2" : ""
            }`}
            aria-label="Toggle sidebar"
          >
            {!isSidebarOpen ? (
              <PanelLeftOpen
                size={20}
                className="sm:w-6 sm:h-6"
                color="#B9C0B8"
              />
            ) : (
              <PanelLeftClose
                size={20}
                className="sm:w-6 sm:h-6"
                color="#B9C0B8"
              />
            )}
          </button>
        </div>

        <div className="flex flex-grow h-full bg-[#e9f1e8]">
          {/* Center Section - Search */}
          <div className="flex items-center flex-grow mx-2 sm:mx-3">
            {/* Desktop Search Bar
            <div className="hidden sm:flex items-center bg-[#f9fafb] rounded-md px-3 py-1.5 w-full max-w-xs lg:max-w-sm xl:w-80">
              <Search
                size={20}
                strokeWidth={0.75}
                absoluteStrokeWidth
                className="mr-2"
                fill="none"
                color="#7C837B"
              />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search…"
                className="bg-transparent outline-none text-sm text-[#344054] placeholder:text-[#b9c0b8] flex-grow"
              />
            </div>

            Mobile Search Button
            <button
              onClick={() => setIsSearchOpen(!isSearchOpen)}
              className="flex sm:hidden items-center justify-center p-2 hover:bg-[#f9fafb] rounded-lg"
              aria-label="Search"
            >
              <Search size={20} color="#7C837B" />
            </button> */}
          </div>

          {/* Right Section */}
          <div className="flex items-center gap-1 sm:gap-3 mx-1 sm:mx-3">
            {/* Customer/Tenant Selector - only for multi-customer users (e.g. super admin) */}
            {isMulti && (
              <div className="relative hidden md:block">
                <button
                  onClick={() => setIsTenantOpen(!isTenantOpen)}
                  className="flex items-center gap-1.5 px-2 sm:px-3 py-1.5 rounded-md hover:bg-[#f9fafb] transition-colors"
                  disabled={isSwitchingTenant}
                >
                  <Building2 size={20} color="#B9C0B8" />
                  <span className="hidden lg:inline text-sm font-medium text-[#667085]">
                    {isSwitchingTenant
                      ? "Switching..."
                      : activeTenant?.customer_name || "Select Customer"}
                  </span>
                  {!isTenantOpen ? (
                    <ChevronDown
                      size={20}
                      color="#B9C0B8"
                      className="hidden lg:block"
                    />
                  ) : (
                    <ChevronUp
                      size={20}
                      color="#B9C0B8"
                      className="hidden lg:block"
                    />
                  )}
                </button>

                {isTenantOpen && tenants.length > 0 && (
                  <div
                    className="absolute top-full mt-2 right-0 bg-white border border-[#d0d5dd] rounded-lg py-1 min-w-[200px] max-h-60 overflow-y-auto z-20"
                    style={{ boxShadow: "0px 4px 6px -2px rgba(16,24,40,0.1)" }}
                  >
                    {tenants.map((tenant) => {
                      const provisioned = !!tenant.schema_name;
                      return (
                        <button
                          key={tenant.customer_id}
                          onClick={() =>
                            provisioned &&
                            handleSelectTenant(tenant.customer_id)
                          }
                          disabled={!provisioned}
                          className={`w-full text-left px-4 py-2 text-sm ${
                            !provisioned
                              ? "text-[#b9c0b8] cursor-not-allowed"
                              : tenant.customer_id === activeCustomerId
                                ? "bg-blue-50 text-blue-700 font-medium"
                                : "text-[#344054] hover:bg-[#f9fafb]"
                          }`}
                        >
                          {tenant.customer_name}
                          {!provisioned && (
                            <span className="block text-xs text-[#b9c0b8]">
                              No data provisioned
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Location Selector - Hidden on mobile, shown in sidebar */}
            <div className="relative hidden md:block">
              <button
                onClick={() => setIsLocationOpen(!isLocationOpen)}
                className="flex items-center gap-1.5 px-2 sm:px-3 py-1.5 rounded-md hover:bg-[#f9fafb] transition-colors"
                disabled={regionsLoading}
              >
                <MapPin size={20} color="#B9C0B8" />
                <span className="hidden lg:inline text-sm font-medium text-[#667085]">
                  {regionsLoading
                    ? "Loading..."
                    : selectedRegion?.region_name ||
                      "Select Location"}
                </span>
                {!isLocationOpen ? (
                  <ChevronDown
                    size={20}
                    color="#B9C0B8"
                    className="hidden lg:block"
                  />
                ) : (
                  <ChevronUp
                    size={20}
                    color="#B9C0B8"
                    className="hidden lg:block"
                  />
                )}
              </button>

              {isLocationOpen &&
                !regionsLoading &&
                regions.length > 0 && (
                  <div
                    className="absolute top-full mt-2 right-0 bg-white border border-[#d0d5dd] rounded-lg py-1 min-w-[180px] max-h-60 overflow-y-auto z-20"
                    style={{ boxShadow: "0px 4px 6px -2px rgba(16,24,40,0.1)" }}
                  >
                    {regions.map((region) => (
                      <button
                        key={region.region_id}
                        onClick={() => {
                          setSelectedRegion(region);
                          setIsLocationOpen(false);
                        }}
                        className={`w-full text-left px-4 py-2 hover:bg-[#f9fafb] text-sm ${
                          selectedRegion?.region_id ===
                          region.region_id
                            ? "bg-blue-50 text-blue-700 font-medium"
                            : "text-[#344054]"
                        }`}
                      >
                        {region.region_name}
                      </button>
                    ))}
                  </div>
                )}
            </div>

            {/* Notifications */}
            <NotificationBell />

            {/* Help - Hidden on mobile */}
            <button
              onClick={go_to_support}
              className="hidden sm:block p-2 hover:bg-[#f9fafb] rounded-lg transition-colors"
              aria-label="Help"
            >
              <CircleQuestionMark size={20} color="#7C837B" />
            </button>

            {/* User Section */}
            <div className="flex items-center gap-1 sm:gap-3 relative">
              {/* Role Badge - Hidden on small mobile */}
              <span
                className={`hidden sm:inline-block px-2 py-1 rounded-full text-xs font-medium ${getRoleBadgeColor()}`}
              >
                {userFirstName || user?.username}
              </span>

              {/* User Avatar and Dropdown */}
              <div className="relative">
                <button
                  onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                  className="flex items-center gap-2"
                  aria-label="User menu"
                >
                  <div
                    className={`w-6 h-6 sm:w-7 sm:h-7 rounded-full flex items-center justify-center text-white text-xs sm:text-sm font-medium ${getAvatarColor()} relative`}
                  >
                    {userFirstName ? userFirstName[0].toUpperCase() : user?.username ? user.username[0].toUpperCase() : "U"}
                    <div className="absolute bottom-0 right-0 w-2 h-2 sm:w-3 sm:h-3 bg-[#12B76A] rounded-full border sm:border-2 border-white" />
                  </div>
                </button>

                <UserDropdown
                  isOpen={isUserMenuOpen}
                  onClose={() => setIsUserMenuOpen(false)}
                  userGroups={userGroups}
                  userFirstName={userFirstName}
                  userLastName={userLastName}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Search Overlay */}
      {isSearchOpen && (
        <div className="fixed top-14 left-0 right-0 bg-white border-b border-gray-200 p-3 z-20 sm:hidden">
          <div className="flex items-center bg-[#f9fafb] rounded-md px-3 py-2">
            <Search
              size={18}
              strokeWidth={0.75}
              className="mr-2"
              fill="none"
              color="#7C837B"
            />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search…"
              className="bg-transparent outline-none text-sm text-[#344054] placeholder:text-[#b9c0b8] flex-grow"
              autoFocus
            />
            <button
              onClick={() => setIsSearchOpen(false)}
              className="ml-2 p-1"
              aria-label="Close search"
            >
              <X size={18} color="#7C837B" />
            </button>
          </div>
        </div>
      )}

      {/* Sidebar Component */}
      <Sidebar
        isOpen={isSidebarOpen}
        onClose={() => setIsSidebarOpen(false)}
        userGroups={userGroups}
        selectedLocation={selectedRegion}
        onLocationChange={setSelectedRegion}
        regions={regions}
        regionsLoading={regionsLoading}
      />
    </>
  );
}
